import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { optimizeImage } from "../../lib/optimizeImage";

const ease = [0.16, 1, 0.3, 1];
export const srcOf = (img) => (typeof img === "string" ? img : img.src);

export const useAspects = (urls) => {
    const key = urls.join("|");
    const [aspects, setAspects] = useState({});
    useEffect(() => {
        let alive = true;
        urls.forEach((u) => {
            const im = new Image();
            im.onload = () => alive && setAspects((a) => ({ ...a, [u]: im.naturalWidth / im.naturalHeight }));
            im.src = u;
        });
        return () => {
            alive = false;
        };
    }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
    return aspects;
};

const Img = ({ src, alt, testid }) => (
    <img
        src={src}
        alt={alt}
        loading="lazy"
        decoding="async"
        data-testid={testid}
        className="block w-full h-auto rounded-lg md:rounded-xl"
    />
);

export const FullImage = ({ img, alt, testid }) => (
    <motion.figure
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.9, ease }}
        className="max-w-[1400px] mx-auto px-4 md:px-8"
    >
        <Img src={optimizeImage(srcOf(img), 2000)} alt={alt} testid={testid} />
    </motion.figure>
);

// Equal-height row: each image grows by its aspect ratio, so nothing is cropped.
export const ImageRow = ({ images, alt, testid }) => {
    const urls = images.map((img) => optimizeImage(srcOf(img), 1400));
    const aspects = useAspects(urls);
    return (
        <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease }}
            className="max-w-[1400px] mx-auto px-4 md:px-8 flex flex-col md:flex-row md:items-start gap-4 md:gap-6"
            data-testid={testid}
        >
            {urls.map((u, i) => (
                <div key={u + i} className="min-w-0" style={{ flex: `${aspects[u] || 1} 1 0%` }}>
                    <Img src={u} alt={`${alt} ${i + 1}`} />
                </div>
            ))}
        </motion.div>
    );
};

export const TextBlock = ({ label, children, testid }) => (
    <motion.section
        initial={{ opacity: 0, y: 18 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.8, ease }}
        data-testid={testid}
    >
        <h2 className="font-accent text-coral text-3xl md:text-4xl leading-none inline-block rotate-[-2deg]">
            {label}
        </h2>
        <div className="mt-4 font-body text-base md:text-lg leading-[1.75] text-text-primary/85 space-y-4">{children}</div>
    </motion.section>
);
